import { Button } from "@/components/Button/Button";
import Gallery from "@/components/Gallery/Gallery";
import { FC } from "react";
import Text from "../Text/Text";
import { getColPosContainer } from "utils/get-col-pos-container";
import { cva } from "class-variance-authority";

interface PositionI {
  horizontal: "left" | "right" | "center";
}
interface GalleryI {
  position: PositionI;
}
interface ContentI {
  gallery: GalleryI;
  bodytext: string;
  header: string;
  subheader: string;
  headerLink: HeaderLinkI;
}

interface HeaderLinkI {
  href: string;
  target?: string;
  class?: string;
  title: string;
  linkText?: string;
  additionalAttributes?: any[];
}

const TextImage: FC<{
  content: ContentI;
  id: string;
  appearance?: any;
  colPos?: number;
  parentBackgroundColor?: string;
  className?: string;
}> = ({
  content,
  id,
  appearance,
  colPos,
  parentBackgroundColor,
  className = "",
  ...rest
}) => {
  const { gallery, headerLink } = content;
  const { position } = gallery;
  const { layout = "default" } = appearance;
  const isBlackBackground = parentBackgroundColor === "7";
  const isBlackBG = className.includes("bg-black");

  const imagePosition = position?.horizontal;
  let parentBackgroundMode = "default" as "default" | "light" | "dark";
  if (layout === "layout-1") {
    if (isBlackBackground) {
      parentBackgroundMode = "dark";
    } else {
      parentBackgroundMode = "light";
    }
  }

  const containerClasses = cva("", {
    variants: {
      layout: {
        default: "",
        "layout-1": "",
        "layout-2": "lg:w-full lg:max-w-none",
      },
    },
    defaultVariants: {
      layout: "default",
    },
  });
  const gridClasses = cva("grid grid-cols-1 relative", {
    variants: {
      layout: {
        default: "",
        "layout-1": "rounded-2xl overflow-hidden",
        "layout-2": "",
      },
      imagePosition: {
        left: "md:grid-cols-2",
        right: "md:grid-cols-2",
        center: "",
      },
    },
    defaultVariants: {
      layout: "default",
      imagePosition: "center",
    },
  });
  const imageColClasses = cva("relative", {
    variants: {
      layout: {
        default: "",
        "layout-1": "",
        "layout-2": "",
      },
      imagePosition: {
        left: "",
        right: "md:order-2",
        center: "",
      },
    },
    compoundVariants: [
      {
        layout: "layout-2",
        imagePosition: "left",
        className: "-ml-5 lg:mr-0 -mr-5 lg:-ml-7",
      },
      {
        layout: "layout-2",
        imagePosition: "right",
        className: "-mr-5 lg:ml-0 -ml-5 lg:-mr-7",
      },
      {
        layout: "layout-2",
        imagePosition: "center",
        className: "-mx-5 lg:-mx-7",
      },
    ],
    defaultVariants: {
      layout: "default",
      imagePosition: "center",
    },
  });
  const textColClasses = cva("flex flex-col justify-center py-6 md:py-12", {
    variants: {
      layout: {
        default: "",
        "layout-1": "px-6 md:px-12",
        "layout-2": "",
      },
      imagePosition: {
        left: "md:pl-12",
        right: "md:pr-12",
        center: "",
      },
      parentBackgroundMode: {
        light: "bg-gray-100",
        dark: "bg-gray-600",
        default: "",
      },
    },
    compoundVariants: [
      {
        layout: "layout-2",
        imagePosition: "left",
        className:
          "lg:w-[calc(var(--container-width)*0.5-var(--container-padding))] lg:mr-auto",
      },
      {
        layout: "layout-2",
        imagePosition: "right",
        className:
          "lg:w-[calc(var(--container-width)*0.5-var(--container-padding))] lg:ml-auto",
      },
      {
        layout: "layout-2",
        imagePosition: "center",
        className:
          "lg:w-[calc(var(--container-width)-var(--container-padding)*2)] lg:mx-auto",
      },
    ],
    defaultVariants: {
      layout: "default",
      imagePosition: "center",
      parentBackgroundMode: "default",
    },
  });

  const isLeft = position?.horizontal === "left";
  const isRight = position?.horizontal === "right";
  const isAbove = position?.horizontal === "center";

  switch (layout) {
    case "layout-1":
      return (
        <div className="@container" id={`c${id}`}>
          <div
            className={`${getColPosContainer(
              colPos
            )} w-full grid grid-cols-1 relative xl:grid-cols-2  h-full`}
          >
            <div className="h-full order-1 md:order-none @3xl:!order-none">
              <ImageCol gallery={gallery} hasBorderRadius={true} />
            </div>
            <div
              className={`${
                parentBackgroundColor === "7" ? "bg-gray-600" : "bg-gray-100"
              } xl:px-12 xl:py-15 lg:px-8 lg:py-10 px-6 py-7  flex flex-col justify-center h-full order-2 md:order-none @3xl:!order-none xl:rounded-tr-2xl xl:rounded-bl-none rounded-br-2xl rounded-bl-2xl`}
            >
              <Text
                content={content}
                noPadding={true}
                parentBackgroundColor={parentBackgroundColor}
              />
              <OptionalButton
                {...headerLink}
                isBlackBackground={isBlackBackground}
              />
            </div>
          </div>
        </div>
      );
    case "layout-2":
      return (
        <div
          className={`@container text-image--layout-2 ${className} ${
            isBlackBG ? "[&>*>h3]:!text-alert" : ""
          }`}
          id={`c${id}`}
        >
          <div
            className={`${getColPosContainer(colPos)} ${containerClasses({
              layout,
            })}`}
          >
            <div className={gridClasses({ layout, imagePosition })}>
              <div className={imageColClasses({ layout, imagePosition })}>
                <ImageCol gallery={gallery} />
              </div>
              <div
                className={textColClasses({
                  layout,
                  imagePosition,
                  parentBackgroundMode,
                })}
              >
                <Text
                  content={content}
                  noPadding={true}
                  parentBackgroundColor={isBlackBG ? "7" : "0"}
                />
                <OptionalButton
                  {...headerLink}
                  isBlackBackground={isBlackBackground}
                />
              </div>
            </div>
          </div>
        </div>
      );
    default:
      return (
        <div className="@container" id={`c${id}`}>
          <div
            className={`${getColPosContainer(
              colPos
            )} w-full grid grid-cols-1  relative ${
              isAbove ? "" : "md:grid-cols-2 @3xl:!grid-cols-2"
            }`}
          >
            {isAbove && <ImageCol gallery={gallery} />}
            {isLeft && <ImageCol gallery={gallery} />}
            <div
              className={`${
                isLeft ? "@3xl:!pl-16 md:pl-16" : "md:pr-16 @3xl:!pr-16"
              } py-6 flex flex-col justify-center h-full order-2 md:order-none @3xl:!order-none `}
            >
              <Text
                content={content}
                noPadding={true}
                parentBackgroundColor={parentBackgroundColor}
              />
              <OptionalButton
                {...headerLink}
                isBlackBackground={isBlackBackground}
              />
            </div>
            {isRight && <ImageCol gallery={gallery} />}
          </div>
        </div>
      );
  }
};

const ImageCol = ({ gallery, hasBorderRadius = false }) => (
  <div className="flex flex-col justify-center h-full w-full">
    <Gallery {...gallery} hasBorderRadius={hasBorderRadius} />
  </div>
);
const OptionalButton = ({ href, title, isBlackBackground }) => {
  return (
    <>
      {href && title && (
        <div className="lg:container flex justify-start items-center lg:mt-9 mt-7 lg:mb-0 mb-6">
          <Button
            as="a"
            href={href}
            className={`lg:w-auto w-full ${
              isBlackBackground ? "text-white" : ""
            }`}
          >
            {title}
          </Button>
        </div>
      )}
    </>
  );
};

export default TextImage;
